import React from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Button, Combobox, Dialog, Icon } from '@node-space/ui-components'
import { useFiltersModal } from 'hooks/useFiltersModal'
import { EmbeddedCustomerStatus } from 'pages/EmbeddedCustomers/helpers/types'
import { getCustomerStatusLabel, getCustomerTypeLabel } from 'pages/EmbeddedCustomers/helpers/utils'
import { StatusLabelMapping } from 'pages/NestedCustomers/constants'
import { CustomerEntityType, CustomersFilters } from 'types/customers'

interface CustomersV1FiltersModalProps {
  statusFilters: string[]
  appliedFilters: CustomersFilters
  isEmbeddedOrReliance: boolean
  onApply: (filters: Partial<CustomersFilters>) => void
  onResetAll: () => void
}

export const CustomersV1FiltersModal = ({
  statusFilters,
  appliedFilters,
  isEmbeddedOrReliance,
  onApply,
  onResetAll,
}: CustomersV1FiltersModalProps) => {
  const { t } = useTranslation()
  const {
    isOpen,
    setIsOpen,
    localStatuses,
    localTypes,
    toggleStatus,
    toggleType,
    handleOpen,
    handleApply,
    handleReset,
  } = useFiltersModal({
    appliedFilters,
    onApply: ({ statuses, types }) => onApply({ statuses, types: types as CustomerEntityType[] }),
    onResetAll,
  })

  const appliedCount =
    (appliedFilters?.statuses?.length ?? 0) + (appliedFilters?.types?.length ?? 0)

  const getStatusLabel = (status: string) =>
    isEmbeddedOrReliance
      ? getCustomerStatusLabel(status as EmbeddedCustomerStatus, t)
      : StatusLabelMapping?.[status as keyof typeof StatusLabelMapping]

  return (
    <>
      <Button
        variant="secondary"
        iconElement={<Icon name="Funnel" />}
        onClick={handleOpen}
        testId="v1-filters-btn"
      >
        {appliedCount ? `${t('filters')} (${appliedCount})` : t('filters')}
      </Button>

      <Dialog
        title={t('customers.v1FiltersTitle')}
        open={isOpen}
        onClose={() => setIsOpen(false)}
        type="utility"
        ActionButtons={[
          <Button key="show" onClick={handleApply} testId="v1-filters-apply-btn">
            {t('show')}
          </Button>,
          <Button
            key="reset"
            variant="secondary"
            onClick={handleReset}
            testId="v1-filters-reset-btn"
          >
            {t('resetAll')}
          </Button>,
        ]}
      >
        <Box flexCol gapY={16}>
          <Combobox
            id="customer-v1-filter-statuses"
            label={t('status')}
            selectPlaceholder={t('status')}
            filter={false}
            modal
            checkedValues={localStatuses}
            onCheckedChange={value => toggleStatus(value)}
            items={statusFilters.map(status => ({
              value: status,
              leftText: getStatusLabel(status),
            }))}
            testId="customer-v1-filter-statuses"
          />
          <Combobox
            id="customer-v1-filter-types"
            label={t('customers.type')}
            selectPlaceholder={t('customers.type')}
            filter={false}
            modal
            checkedValues={localTypes}
            onCheckedChange={value => toggleType(value)}
            items={Object.values(CustomerEntityType).map(type => ({
              value: type,
              leftText: getCustomerTypeLabel(type, t),
            }))}
            testId="customer-v1-filter-types"
          />
        </Box>
      </Dialog>
    </>
  )
}
